'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { closeNotificationCentre, useNotificationCentreOpen } from '../../model/notification-centre-store';
import { NotificationCentre } from './notification-centre';

/**
 * The centre laid over whatever page the person was reading.
 *
 * ═══ WHAT IT REPLACED ═══
 *
 * The centre was a section: pressing the bell's footer unmounted the register,
 * the board or the record and mounted the days in its place, with no rail item
 * to press to get back. Somebody who opened it to check one thing lost their
 * scroll, their filters and their place, and the only way home was the browser's
 * back button.
 *
 * ═══ WHY IT IS A ROOT OVERLAY HERE, AND NOT A COVER IN THE TREE ═══
 *
 * On the book this was a cover INSIDE the shell, wrapped around the section, so
 * the section stayed mounted underneath with its scroll and its selection while
 * the days were over it. The platform has no such place: `AppShell` switches on
 * identity and every route supplies its own chrome, so there is no one element
 * every page's content passes through that a cover could wrap.
 *
 * A fixed overlay mounted once at the root gets the same guarantee for free,
 * and gets it more strongly. The page is never unmounted, never re-rendered and
 * never told anything happened: its scroll, its filters, any open dialog and
 * any in-flight request are exactly as they were, because nothing about the
 * page changed. Closing the centre removes one element. That is the whole
 * mechanism, and it is less machinery than the phase-plus-inert dance the cover
 * needed to fake it.
 *
 * ONE CONSEQUENCE, RECORDED: an overlay at the root sits over the navbar too,
 * so the centre covers the bell that opened it. That is right rather than a
 * cost. The bell is the glance and the centre is the sit-down, and the centre's
 * own header carries Close and Escape, so nothing behind it needs pressing.
 *
 * ═══ THE DISSOLVE IS LOCAL ═══
 *
 * The book's arrival was `useDissolve` from its agent-artefact leaf, a mask
 * animation shared by the two surfaces that shell mounts. There is one surface
 * here and that leaf is the book's, so the arrival is a CSS opacity and blur
 * transition on the overlay, held behind a phase so the element is in the tree
 * for a frame before it is asked to animate. Nothing measures and nothing
 * schedules.
 *
 * IT DOES NOT DRAW PAPER. A document is a page and gets a white sheet; the
 * centre's days are cards floating on a wash, so the cover is a frosted layer
 * and the page shows through it.
 */

type Phase = 'closed' | 'in' | 'open' | 'out';

/** Long enough to read as a dissolve, short enough not to be a wait. */
const DISSOLVE_MS = 180;
export function NotificationCentreSurface() {
  const $ = _c(11);
  const open = useNotificationCentreOpen();
  const [phase, setPhase] = useState(open ? "open" : "closed");
  const [lastOpen, setLastOpen] = useState(open);
  if (lastOpen !== open) {
    setLastOpen(open);
    setPhase(open ? "in" : "out");
  }
  let t0;
  let t1;
  if ($[0] !== phase) {
    t0 = () => {
      if (phase !== "in" && phase !== "out") {
        return;
      }
      const settle = phase === "in" ? requestAnimationFrame(() => setPhase("open")) : null;
      const leave = phase === "out" ? setTimeout(() => setPhase("closed"), DISSOLVE_MS) : null;
      return () => {
        if (settle !== null) {
          cancelAnimationFrame(settle);
        }
        if (leave !== null) {
          clearTimeout(leave);
        }
      };
    };
    t1 = [phase];
    $[0] = phase;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const onKeyDown = _temp;
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    };
    t3 = [open];
    $[3] = open;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (phase === "closed") {
    return null;
  }
  const t4 = phase === "open" ? "opacity-100" : "pointer-events-none opacity-0";
  let t5;
  if ($[6] !== t4) {
    t5 = cn("fixed inset-0 z-40 flex min-h-0 flex-col bg-white/70 backdrop-blur-xl transition-opacity duration-200 ease-out dark:bg-[#0b1020]/70", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <NotificationCentre />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <div data-notifications-take="surface" className={t5}>{t6}</div>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function _temp(event) {
  if (event.key !== "Escape") {
    return;
  }
  event.preventDefault();
  closeNotificationCentre();
}